import React from 'react';
import { Link } from 'react-router-dom';
import resolveHeaderLogo from 'utilities/headerLogo';
import { themedFrame } from './useSiteTheme';
import SiteNav from './SiteNav';
import SiteNavMobile from './SiteNavMobile';
import useIsNarrow from 'hooks/useIsNarrow';
import { headerStyle, footerStyle, glassBackground, buttonSkin } from './headerStyle';

/**
 * The brand's global header and footer, as a visitor sees them.
 *
 * Plain elements and inline styles on purpose. This renders on the brand's
 * own domain, outside the dashboard's MUI theme, and it has to show the
 * colours the brand chose rather than anything inherited from ours — a
 * themed component here would quietly repaint a customer's site.
 *
 * `header` or `footer` arriving as null means the brand switched it off, or
 * this page opted out. The server resolves both of those, so there is nothing
 * to decide here beyond whether the object exists.
 */

const navLink = (color) => ({
  color,
  textDecoration: 'none',
  fontSize: 14,
  opacity: 0.85,
  whiteSpace: 'nowrap',
});

function Links({ links, color, size }) {
  return (links || [])
    .filter((l) => l.label && l.url)
    .map((l, i) => (
      <Link
        key={i}
        to={l.url}
        target={l.newTab ? '_blank' : undefined}
        rel={l.newTab ? 'noopener noreferrer' : undefined}
        style={{ ...navLink(color), fontSize: size }}
      >
        {l.label}
      </Link>
    ));
}

export function SiteHeader({ header, brandName, brandLogo, theme }) {
  // Before the early return: a hook has to run on every render of this
  // component or React loses track of which state belongs to what.
  //
  // A row of links has nowhere to go at phone width, and the desktop menu's
  // submenus open on hover — which does not exist on a touch screen. Below
  // this the bar collapses to a button and the menu becomes a panel.
  const narrow = useIsNarrow(860);

  if (!header) return null;

  /**
   * The bar's colours for the theme the visitor is actually in.
   *
   * Everything below reads `ink` and `ground` rather than the owner's own
   * two fields. Reading those directly is what left a dark site with a
   * near-black menu on a near-black bar — and with the theme toggle, drawn
   * in the same ink, invisible rather than absent.
   */
  const { bg: ground, fg: ink } = themedFrame(header, theme?.isDark);
  const title = (header.title || '').trim() || brandName || '';
  const logo = resolveHeaderLogo(header, brandLogo);

  const layout = header.layout || 'left';
  const links = (header.links || []).filter((l) => l.label && l.url);
  // Every measurement and finish the owner chose, with the defaults applied
  // for headers configured before any of this existed.
  const st = headerStyle(header);

  const mark = (
    <Link
      to='/'
      style={{ color: ink, textDecoration: 'none', display: 'flex', alignItems: 'center', minWidth: 0 }}
    >
      {logo ? (
        <img src={logo.url} alt={title} style={{ height: logo.height, maxWidth: 220, objectFit: 'contain' }} />
      ) : (
        <span style={{ fontWeight: 700, fontSize: 17 }}>{title}</span>
      )}
    </Link>
  );

  // Sits at the end of the bar in every arrangement: it is a preference
  // control, not navigation, and putting it among the menu links invites
  // people to read it as one.
  const themeButton = theme?.enabled ? (
    <button
      type='button'
      onClick={theme.toggle}
      aria-label={theme.isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={theme.isDark ? 'Light mode' : 'Dark mode'}
      style={{
        background: 'transparent',
        border: `1px solid ${ink}`,
        color: ink,
        opacity: 0.75,
        borderRadius: 8,
        width: 32,
        height: 32,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {theme.isDark ? '☀' : '☾'}
    </button>
  ) : null;

  // Extras sit with the theme button at the end of the bar — they are
  // information and actions, not navigation, and mixing them into the menu
  // makes both harder to read.
  const extras = (header.extras || []).filter((x) => x.label).map((x, i) => {
    const common = { fontSize: 14, color: ink, textDecoration: 'none', whiteSpace: 'nowrap' };
    if (x.kind === 'phone') {
      return <a key={i} href={`tel:${x.value}`} style={{ ...common, opacity: 0.85 }}>{x.label}</a>;
    }
    if (x.kind === 'email') {
      return <a key={i} href={`mailto:${x.value}`} style={{ ...common, opacity: 0.85 }}>{x.label}</a>;
    }
    if (x.kind === 'button') {
      const to = x.value || '/contact';
      // One skin for every button in the bar, so three calls to action do
      // not come out looking like three different websites.
      const style = { ...common, ...buttonSkin(st.buttonStyle, ink, ground) };
      return /^https?:\/\//i.test(to)
        ? <a key={i} href={to} style={style} rel='noopener noreferrer'>{x.label}</a>
        : <Link key={i} to={to} style={style}>{x.label}</Link>;
    }
    return <span key={i} style={{ ...common, opacity: 0.75 }}>{x.label}</span>;
  });

  const nav = (items, justify = 'center') => (
    <SiteNav links={items} style={st} color={ink} justify={justify} />
  );

  // 'center' stacks the mark over the menu; 'split' puts half the links
  // either side of it. Both collapse to the plain row at phone width, where
  // there is no room to balance anything.
  // On a phone every arrangement is the same arrangement: the mark on the
  // left, one button on the right. There is no room to balance anything.
  const body = narrow ? (
    <>
      {mark}
      <SiteNavMobile
        links={links}
        style={st}
        color={ink}
        background={ground}
        extras={extras}
        themeButton={themeButton}
      />
    </>
  ) : layout === 'center' ? (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, width: '100%' }}>
      {mark}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
        {nav(links)}
        {extras}
        {themeButton}
      </div>
    </div>
  ) : layout === 'split' ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 28, width: '100%', flexWrap: 'wrap' }}>
      {nav(links.slice(0, Math.ceil(links.length / 2)))}
      {mark}
      {nav(links.slice(Math.ceil(links.length / 2)))}
      {extras}
      {themeButton}
    </div>
  ) : (
    <>
      {mark}
      <div style={{ display: 'flex', alignItems: 'center', gap: st.brandGap, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {nav(links, 'flex-end')}
        {extras}
        {themeButton}
      </div>
    </>
  );

  return (
    <header
      style={{
        ...glassBackground(st, ground),
        color: ink,
        position: header.sticky ? 'sticky' : 'static',
        top: 0,
        zIndex: 20,
        minHeight: st.height,
        borderBottom: st.border ? '1px solid rgba(127,127,127,0.18)' : 'none',
        boxShadow: st.shadow ? '0 2px 14px rgba(0,0,0,0.10)' : 'none',
        fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
      }}
    >
      {/* An inner rail so the bar's background can run edge to edge while its
          contents stay within the width and gutter the owner set. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: st.brandGap,
        flexWrap: 'wrap',
        width: '100%',
        maxWidth: st.maxWidth || '100%',
        margin: '0 auto',
        padding: `10px ${st.gutter}px`,
        minHeight: st.height,
        boxSizing: 'border-box',
      }}
      >
        {body}
      </div>
    </header>
  );
}

export function SiteFooter({ footer, brandName, theme }) {
  if (!footer) return null;
  const ft = footerStyle(footer);
  // Same as the header: the footer is part of the frame, and a frame that
  // stays light around a dark page is worse than no dark mode at all.
  const { bg: ground, fg: ink } = themedFrame(footer, theme?.isDark);

  return (
    <footer
      style={{
        ...glassBackground(ft, ground),
        color: ink,
        padding: `${ft.padding}px ${ft.gutter}px`,
        marginTop: 'auto',
        borderTop: ft.border ? '1px solid rgba(127,127,127,0.18)' : 'none',
        fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
        textAlign: ft.align,
      }}
    >
      {/* One rail, so the background runs edge to edge while the contents
          keep to the width the owner set. */}
      <div style={{ maxWidth: ft.maxWidth || '100%', margin: '0 auto' }}>
      {(footer.columns || []).length > 0 ? (
        <div
          style={{
            display: 'grid',
            // min() so a column narrower than the screen cannot overflow it —
            // the row drops to fewer columns instead, down to one on a phone.
            gridTemplateColumns: `repeat(auto-fit, minmax(min(${ft.minColumnWidth}px, 100%), 1fr))`,
            gap: ft.columnGap,
            marginBottom: 22,
          }}
        >
          {footer.columns.map((col, i) => (
            <div key={i}>
              {col.logoUrl && (
                <img
                  src={col.logoUrl}
                  alt=''
                  style={{ height: 34, maxWidth: 160, objectFit: 'contain', marginBottom: 10 }}
                />
              )}
              {col.title && (
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{col.title}</div>
              )}
              {col.text && (
                <p style={{ margin: '0 0 10px', fontSize: 13, opacity: 0.75, lineHeight: 1.6 }}>
                  {col.text}
                </p>
              )}
              {(col.links || []).length > 0 && (
                <nav style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: ft.linkGap,
                  alignItems: ft.align === 'center' ? 'center' : 'flex-start',
                }}>
                  <Links links={col.links} color={ink} size={ft.fontSize} />
                </nav>
              )}
            </div>
          ))}
        </div>
      ) : (footer.links || []).length > 0 && (
        <nav style={{
          display: 'flex',
          gap: ft.columnGap,
          flexWrap: 'wrap',
          marginBottom: 14,
          justifyContent: ft.align === 'center' ? 'center' : 'flex-start',
        }}>
          <Links links={footer.links} color={ink} size={ft.fontSize} />
        </nav>
      )}
      <div style={{ fontSize: ft.fontSize, opacity: 0.72 }}>
        {(footer.text || '').trim() || `© ${new Date().getFullYear()} ${brandName || ''}`.trim()}
      </div>
      </div>
    </footer>
  );
}
